import { useMutation } from '@tanstack/react-query';
import { userVerificationApi } from '../../services/userVerificationApi';
import { companyRelationshipApi } from '../../services/companyRelationshipApi';
import {
  ALLOWED_FILE_TYPES,
  getMaxFileSize,
  isValidFileType,
} from '../../../features/user-verifications/types';

interface UploadRelationshipVerificationParams {
  relationshipId: string;
  companyVerificationId: string;
  file: File;
}

/**
 * Uploads a document for a relationship-level verification requirement —
 * the provider's own company_admin submitting on behalf of the whole
 * company (see relationshipVerificationController on the backend). Same
 * 3-step presigned-URL flow as useUploadVerification, but step 1 reuses
 * the same upload-url endpoint (it doesn't check company membership) and
 * step 3 posts to the relationship-scoped submit endpoint instead of the
 * per-user one.
 */
export const useSubmitRelationshipVerification = () => {
  return useMutation({
    mutationFn: async ({
      relationshipId,
      companyVerificationId,
      file,
    }: UploadRelationshipVerificationParams) => {
      if (!isValidFileType(file.type)) {
        throw new Error(
          `Tipo de archivo no permitido. Tipos permitidos: ${ALLOWED_FILE_TYPES.join(', ')}`
        );
      }

      const maxSize = getMaxFileSize(file.type);
      if (file.size > maxSize) {
        const maxSizeMB = maxSize / (1024 * 1024);
        throw new Error(`El archivo excede el tamaño máximo de ${maxSizeMB}MB`);
      }

      const uploadUrlResponse = await userVerificationApi.getUploadUrl(
        'company',
        companyVerificationId,
        file.type,
        file.name
      );
      if (!uploadUrlResponse.success || !uploadUrlResponse.data) {
        throw new Error(uploadUrlResponse.error || 'Error al obtener URL de subida');
      }
      const { presignedPost, key } = uploadUrlResponse.data;

      const formData = new FormData();
      Object.entries(presignedPost.fields).forEach(([fieldKey, value]) => {
        formData.append(fieldKey, value as string);
      });
      formData.append('file', file);

      const s3Response = await fetch(presignedPost.url, { method: 'POST', body: formData });
      if (!s3Response.ok) {
        throw new Error('Error al subir el archivo a S3');
      }

      return companyRelationshipApi.submitVerification(relationshipId, companyVerificationId, {
        fileKey: key,
        fileName: file.name,
        fileMimeType: file.type,
        fileSize: file.size,
      });
    },
  });
};

/**
 * Approve/reject a company-level relationship verification submission
 * (verificationLevel==='company'). Individual-level rows keep using
 * useReviewVerification (useUserVerificationMutations.ts) unchanged.
 */
export const useReviewRelationshipVerification = () => {
  return useMutation({
    mutationFn: (params: {
      relationshipId: string;
      companyVerificationId: string;
      action: 'approve' | 'reject';
      rejectionReason?: string;
      expiryDate?: string;
    }) =>
      companyRelationshipApi.reviewVerification(
        params.relationshipId,
        params.companyVerificationId,
        {
          action: params.action,
          rejectionReason: params.rejectionReason,
          expiryDate: params.expiryDate,
        },
      ),
  });
};

/** Signed URL to view a company-level relationship verification's document. */
export const useViewRelationshipDocument = () => {
  return useMutation({
    mutationFn: (params: { relationshipId: string; companyVerificationId: string }) =>
      companyRelationshipApi.getDocumentDownloadUrl(
        params.relationshipId,
        params.companyVerificationId,
      ),
  });
};
